import React from "react";
import { Link, useParams } from "react-router";
import { Calendar, Clock, ArrowLeft, ArrowRight, Share2, Linkedin, Facebook, Twitter, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MikassaLogo } from "@/components/mikassa/Logo";
import SEO from "@/components/SEO";
import blogData from "@/data/blog-posts.json";
import "@/styles/markdown.css";

// Create lookup map from generated blog data
const BLOG_POSTS = {};
const ALL_POSTS = [];

blogData.fr.forEach(post => {
  BLOG_POSTS[post.slug] = {
    ...post,
    image: post.coverImage.startsWith('/')
      ? `https://images.pexels.com/photos/5642426/pexels-photo-5642426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200`
      : post.coverImage,
  };
  ALL_POSTS.push({
    slug: post.slug,
    title: post.title,
    image: post.coverImage.startsWith('/')
      ? `https://images.pexels.com/photos/5642426/pexels-photo-5642426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300`
      : post.coverImage,
  });
});

const BlogArticle = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS[slug];

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading font-black text-4xl text-[#0C2340] mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-[#E8611A] hover:underline">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbs = [
    { name: "Accueil", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: post.title, url: `/blog/${slug}` }
  ];

  // Build alternate language links for hreflang
  const alternateLanguages = [];
  if (post.translationRef) {
    if (post.translationRef.en) {
      alternateLanguages.push({ lang: "en", url: `/en/blog/${post.translationRef.en}` });
    }
    if (post.translationRef.fr) {
      alternateLanguages.push({ lang: "fr", url: `/blog/${post.translationRef.fr}` });
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <SEO
        title={post.title}
        description={post.description}
        image={post.image}
        url={`/blog/${slug}`}
        type="article"
        lang="fr"
        alternateLanguages={alternateLanguages}
        article={{
          title: post.title,
          description: post.description,
          author: post.author,
          publishedAt: post.publishedAt || "2024-03-20",
          tags: post.tags || [post.category],
          keywords: post.seo?.keywords || [post.category, "gestion locative", "immobilier", "Afrique"]
        }}
        breadcrumbs={breadcrumbs}
      />

      {/* Header */}
      <header className="bg-[#0C2340] text-white py-4 md:py-6">
        <div className="max-w-[1400px] mx-auto px-4 md:px-16 flex items-center justify-between">
          <Link to="/" className="shrink-0">
            <MikassaLogo inverse className="scale-75 md:scale-100 origin-left" />
          </Link>
          <Link
            to="/blog"
            className="flex items-center gap-2 font-body text-[0.65rem] md:text-[0.72rem] uppercase tracking-[0.15em] md:tracking-[0.22em] font-medium text-white/85 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">Retour au blog</span>
            <span className="sm:hidden">Blog</span>
          </Link>
        </div>
      </header>

      {/* Breadcrumbs */}
      <nav className="bg-[#FAFAF8] border-b border-[#0C2340]/10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-16 py-3">
          <ol className="flex items-center gap-2 text-sm font-body">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.url} className="flex items-center gap-2">
                {index > 0 && <ChevronRight size={14} className="text-[#636E72]" />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-[#636E72] truncate max-w-[200px]">{crumb.name}</span>
                ) : (
                  <Link to={crumb.url} className="text-[#0C2340] hover:text-[#E8611A] transition-colors">
                    {crumb.name}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>

      {/* Article Hero */}
      <div className="relative">
        <div className="aspect-[4/3] sm:aspect-[21/9] md:aspect-[3/1] w-full overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C2340] via-[#0C2340]/70 to-[#0C2340]/30" />
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-16 pb-6 sm:pb-8 md:pb-12">
            <span className="inline-flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-[#E8611A] text-white font-body text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.2em] mb-3 sm:mb-6">
              {post.category}
            </span>
            <h1 className="font-heading font-black text-white text-xl sm:text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-tighter mb-3 sm:mb-6">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-white/70">
              <span className="flex items-center gap-1.5 sm:gap-2 font-body text-xs sm:text-sm">
                <Calendar size={12} className="sm:w-[14px] sm:h-[14px]" />
                {post.date}
              </span>
              <span className="flex items-center gap-1.5 sm:gap-2 font-body text-xs sm:text-sm">
                <Clock size={12} className="sm:w-[14px] sm:h-[14px]" />
                {post.readTime} de lecture
              </span>
              <span className="font-body text-xs sm:text-sm">
                Par {post.author}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-16 py-8 sm:py-12 md:py-16">
        <div className="mikassa-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.content}
          </ReactMarkdown>
        </div>

        {/* Share */}
        <div className="mt-16 pt-8 border-t border-[#0C2340]/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Share2 size={16} className="text-[#636E72]" />
              <span className="font-body text-[0.7rem] uppercase tracking-[0.2em] text-[#636E72]">
                Partager cet article
              </span>
            </div>
            <div className="flex gap-3">
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#0C2340]/15 flex items-center justify-center hover:bg-[#E8611A] hover:border-[#E8611A] hover:text-white transition-colors text-[#0C2340]"
              >
                <Linkedin size={16} />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#0C2340]/15 flex items-center justify-center hover:bg-[#E8611A] hover:border-[#E8611A] hover:text-white transition-colors text-[#0C2340]"
              >
                <Facebook size={16} />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#0C2340]/15 flex items-center justify-center hover:bg-[#E8611A] hover:border-[#E8611A] hover:text-white transition-colors text-[#0C2340]"
              >
                <Twitter size={16} />
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <div className="bg-[#0A1C33] text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16">
          <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tighter mb-8 text-white">
            Articles similaires
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ALL_POSTS.filter(p => p.slug !== slug).slice(0, 2).map((relatedPost) => (
              <Link
                key={relatedPost.slug}
                to={`/blog/${relatedPost.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="group flex gap-4 p-4 border border-white/10 hover:border-[#E8611A]/50 transition-colors"
              >
                <img
                  src={relatedPost.image}
                  alt={relatedPost.title}
                  loading="lazy"
                  className="w-24 h-24 object-cover flex-shrink-0"
                />
                <div className="flex flex-col justify-center">
                  <h3 className="font-heading font-bold text-white group-hover:text-[#E8611A] transition-colors">
                    {relatedPost.title}
                  </h3>
                  <span className="flex items-center gap-2 font-body text-[0.7rem] uppercase tracking-[0.2em] text-[#E8611A] mt-2">
                    Lire <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#0C2340] text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 text-center">
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mb-4">
            Prêt à simplifier votre gestion locative ?
          </h2>
          <p className="font-body text-white/70 mb-8 max-w-xl mx-auto">
            Découvrez comment Mikassa Agency peut transformer votre activité.
          </p>
          <Link to="/#contact" className="mikassa-btn-primary inline-flex">
            Demander une démo gratuite
            <span className="w-2 h-2 rotate-45 bg-white ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogArticle;
